/**
 * Phone Assistant Configuration Component
 * Allows store managers to configure AWS Connect/Lex phone assistant settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import type {
  PhoneConfig,
  PhoneFeatures,
  BusinessHours,
  DayHours,
  PollyVoice,
  PhoneAnalytics
} from '../types/phoneAssistant';
import { POLLY_VOICES } from '../types/phoneAssistant';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';

interface PhoneAssistantConfigProps {
  storeId: string;
  storeName?: string;
  onSave?: (config: PhoneConfig) => void;
}

const DEFAULT_DAY_HOURS: DayHours = {
  open: '09:00',
  close: '21:00',
  closed: false
};

const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  monday: { ...DEFAULT_DAY_HOURS },
  tuesday: { ...DEFAULT_DAY_HOURS },
  wednesday: { ...DEFAULT_DAY_HOURS },
  thursday: { ...DEFAULT_DAY_HOURS },
  friday: { ...DEFAULT_DAY_HOURS },
  saturday: { open: '10:00', close: '22:00', closed: false },
  sunday: { open: '10:00', close: '20:00', closed: false }
};

const PhoneAssistantConfig: React.FC<PhoneAssistantConfigProps> = ({
  storeId,
  storeName,
  onSave
}) => {
  const { getIdToken } = useAuth();
  const [config, setConfig] = useState<PhoneConfig | null>(null);
  const [analytics, setAnalytics] = useState<PhoneAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'features' | 'hours' | 'analytics'>('general');

  // Load configuration
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.phoneConfig(storeId), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No config exists, create default
          setConfig({
            storeId,
            enabled: false,
            features: {
              ordering: true,
              reservations: true,
              waitlist: true,
              storeInfo: true
            },
            voiceId: 'Joanna',
            greetingMessage: "Thank you for calling. How may I help you today?",
            businessHours: DEFAULT_BUSINESS_HOURS,
            transferNumber: '',
            maxOrderTotal: 200,
            requireCallback: false,
            callRecordingEnabled: true,
            transcriptEnabled: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        } else {
          throw new Error('Failed to load phone configuration');
        }
      } else {
        const data = await response.json();
        // Lambda returns the config as a flat object, not wrapped in {config:...}
        setConfig(data?.config || data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // Load analytics
  const loadAnalytics = useCallback(async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(
        `${API_ENDPOINTS.phoneAnalytics(storeId)}?period=week`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  }, [storeId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab, loadAnalytics]);

  // Save configuration
  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.phoneConfig(storeId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      const data = await response.json();
      setConfig(data.config);
      setSuccess('Configuration saved successfully');
      onSave?.(data.config);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Provision phone number
  const handleProvisionNumber = async () => {
    try {
      setProvisioning(true);
      setError(null);

      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.phoneProvision(storeId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ storeId })
      });

      if (!response.ok) {
        throw new Error('Failed to provision phone number');
      }

      const data = await response.json();
      if (config) {
        setConfig({
          ...config,
          phoneNumber: data.phoneNumber,
          connectInstanceId: data.connectInstanceId,
          lexBotId: data.lexBotId,
          lexBotAliasId: data.lexBotAliasId
        });
      }
      setSuccess('Phone number provisioned successfully');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to provision phone number');
    } finally {
      setProvisioning(false);
    }
  };

  // Update config field
  const updateConfig = (field: keyof PhoneConfig, value: any) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  // Update feature toggle
  const updateFeature = (feature: keyof PhoneFeatures, enabled: boolean) => {
    if (!config) return;
    setConfig({
      ...config,
      features: { ...config.features, [feature]: enabled }
    });
  };

  // Update business hours
  const updateHours = (day: keyof BusinessHours, field: keyof DayHours, value: string | boolean) => {
    if (!config) return;
    setConfig({
      ...config,
      businessHours: {
        ...config.businessHours,
        [day]: { ...config.businessHours[day], [field]: value }
      }
    });
  };

  // Copy hours to all days
  const copyHoursToAll = (sourceDay: keyof BusinessHours) => {
    if (!config) return;
    const sourceHours = config.businessHours[sourceDay];
    const newHours: BusinessHours = {
      monday: { ...sourceHours },
      tuesday: { ...sourceHours },
      wednesday: { ...sourceHours },
      thursday: { ...sourceHours },
      friday: { ...sourceHours },
      saturday: { ...sourceHours },
      sunday: { ...sourceHours }
    };
    setConfig({ ...config, businessHours: newHours });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading configuration...</span>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        Failed to load phone assistant configuration
      </div>
    );
  }

  const days: (keyof BusinessHours)[] = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Phone AI Assistant</h2>
            {storeName && <p className="text-gray-600 mt-1">{storeName}</p>}
          </div>
          <div className="flex items-center space-x-4">
            {config.phoneNumber ? (
              <div className="text-right">
                <p className="text-sm text-gray-500">Phone Number</p>
                <p className="font-mono text-lg font-medium text-gray-900">{config.phoneNumber}</p>
              </div>
            ) : (
              <button
                onClick={handleProvisionNumber}
                disabled={provisioning}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {provisioning ? 'Provisioning...' : 'Get Phone Number'}
              </button>
            )}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => updateConfig('enabled', e.target.checked)}
                disabled={!config.phoneNumber}
                className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                {config.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {(['general', 'features', 'hours', 'analytics'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="mx-6 mt-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {success}
        </div>
      )}

      {/* Tab Content */}
      <div className="p-6">
        {/* General Settings Tab */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            {/* Voice Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI Voice
              </label>
              <select
                value={config.voiceId}
                onChange={(e) => updateConfig('voiceId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                {POLLY_VOICES.map((voice: PollyVoice) => (
                  <option key={voice.voiceId} value={voice.voiceId}>
                    {voice.name} ({voice.gender}) {voice.neural ? '- Neural' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Neural voices provide more natural-sounding speech
              </p>
            </div>

            {/* Greeting Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Greeting Message
              </label>
              <textarea
                value={config.greetingMessage}
                onChange={(e) => updateConfig('greetingMessage', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Thank you for calling. How may I help you today?"
              />
              <p className="mt-1 text-sm text-gray-500">
                Message played when a customer calls
              </p>
            </div>

            {/* Transfer Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transfer Number
              </label>
              <input
                type="tel"
                value={config.transferNumber}
                onChange={(e) => updateConfig('transferNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="+1 555-123-4567"
              />
              <p className="mt-1 text-sm text-gray-500">
                Phone number to transfer calls when customer requests a human
              </p>
            </div>

            {/* Max Order Total */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Order Total ($)
              </label>
              <input
                type="number"
                min="0"
                max="10000"
                value={config.maxOrderTotal}
                onChange={(e) => updateConfig('maxOrderTotal', parseInt(e.target.value))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Orders exceeding this amount will be transferred to a human
              </p>
            </div>

            {/* Recording Options */}
            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.callRecordingEnabled}
                  onChange={(e) => updateConfig('callRecordingEnabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Enable call recording
                </span>
              </label>
              <p className="text-sm text-gray-500 ml-6">
                Record calls for quality assurance and training
              </p>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.transcriptEnabled}
                  onChange={(e) => updateConfig('transcriptEnabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Enable call transcripts
                </span>
              </label>
              <p className="text-sm text-gray-500 ml-6">
                Save text transcripts of all calls
              </p>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.requireCallback}
                  onChange={(e) => updateConfig('requireCallback', e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Require callback confirmation for orders
                </span>
              </label>
              <p className="text-sm text-gray-500 ml-6">
                Staff will call back to confirm phone orders before preparing
              </p>
            </div>
          </div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Phone Assistant Features</h3>
              <p className="text-sm text-blue-700">
                Enable or disable specific capabilities for your phone AI assistant.
                Disabled features will prompt the caller to speak with a staff member.
              </p>
            </div>

            <div className="space-y-4">
              {/* Phone Ordering */}
              <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Phone Ordering</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Allow customers to place orders over the phone with AI assistance
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.features.ordering}
                    onChange={(e) => updateFeature('ordering', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Reservations */}
              <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Reservations</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Allow customers to make table reservations via phone
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.features.reservations}
                    onChange={(e) => updateFeature('reservations', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Waitlist */}
              <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Waitlist</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Allow customers to join the waitlist over the phone
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.features.waitlist}
                    onChange={(e) => updateFeature('waitlist', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Store Info */}
              <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Store Information</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Provide hours, location, and other store details automatically
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.features.storeInfo}
                    onChange={(e) => updateFeature('storeInfo', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Business Hours Tab */}
        {activeTab === 'hours' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Phone Hours</h3>
              <p className="text-sm text-blue-700">
                Set when the phone AI assistant is available. Outside these hours,
                calls will go directly to the transfer number.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Day
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Open
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Close
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Closed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {days.map((day) => (
                    <tr key={day} className={config.businessHours[day].closed ? 'bg-gray-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900 capitalize">{day}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="time"
                          value={config.businessHours[day].open}
                          onChange={(e) => updateHours(day, 'open', e.target.value)}
                          disabled={config.businessHours[day].closed}
                          className="px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="time"
                          value={config.businessHours[day].close}
                          onChange={(e) => updateHours(day, 'close', e.target.value)}
                          disabled={config.businessHours[day].closed}
                          className="px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={config.businessHours[day].closed}
                          onChange={(e) => updateHours(day, 'closed', e.target.checked)}
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => copyHoursToAll(day)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Copy to all
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {!analytics ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading analytics...</span>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Total Calls</p>
                    <p className="text-3xl font-bold text-blue-900">{analytics.totalCalls}</p>
                    <p className="text-xs text-blue-600 mt-1">This {analytics.period}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">Orders Placed</p>
                    <p className="text-3xl font-bold text-green-900">{analytics.completedOrders}</p>
                    <p className="text-xs text-green-600 mt-1">${analytics.totalRevenue.toFixed(2)} revenue</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-purple-600 font-medium">Reservations</p>
                    <p className="text-3xl font-bold text-purple-900">{analytics.reservationsMade}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-orange-600 font-medium">Waitlist Additions</p>
                    <p className="text-3xl font-bold text-orange-900">{analytics.waitlistAdditions}</p>
                  </div>
                </div>

                {/* Call Breakdown */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Call Breakdown</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">
                        {analytics.averageCallDuration.toFixed(0)}s
                      </p>
                      <p className="text-sm text-gray-500">Avg Duration</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">
                        {analytics.transfersToHuman}
                      </p>
                      <p className="text-sm text-gray-500">Transferred to Human</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">
                        {analytics.abandonedCalls}
                      </p>
                      <p className="text-sm text-gray-500">Abandoned Calls</p>
                    </div>
                  </div>
                </div>

                {/* Intent Breakdown */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Intent Distribution</h3>
                  <div className="space-y-3">
                    {Object.entries(analytics.intentBreakdown).map(([intent, count]) => {
                      const percentage = analytics.totalCalls > 0
                        ? ((count as number) / analytics.totalCalls * 100).toFixed(1)
                        : '0';
                      return (
                        <div key={intent} className="flex items-center">
                          <span className="w-32 text-sm text-gray-600">{intent}</span>
                          <div className="flex-1 mx-4">
                            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-600 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                          <span className="w-20 text-sm text-gray-900 text-right">
                            {count as number} ({percentage}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Estimated Cost */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">Estimated Cost</h3>
                      <p className="text-2xl font-bold text-yellow-900">
                        ${analytics.estimatedCost.toFixed(2)}
                      </p>
                    </div>
                    <p className="text-sm text-yellow-700">
                      Based on AWS Connect + Lex usage
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
        <button
          onClick={loadConfig}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Reset Changes
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

export default PhoneAssistantConfig;
